import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/* PWA install prompt — listens for beforeinstallprompt + shows a soft toast-style banner.
   On iOS Safari (no beforeinstallprompt), shows an instructive note instead. */

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = localStorage.getItem("kanya_install_dismissed");
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari path — no beforeinstallprompt event
    const ua = window.navigator.userAgent || "";
    const isiOS = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isiOS && isSafari && !standalone) {
      setIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setVisible(false);
    localStorage.setItem("kanya_install_dismissed", "1");
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("kanya_install_dismissed", "1");
  };

  if (!visible) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-[460px] w-[calc(100%-32px)] surface-ivory plush-shadow rounded-[20px] px-4 py-3 flex items-center gap-3 border border-[hsl(var(--border))]"
      data-testid="install-prompt"
    >
      <div className="shrink-0 w-10 h-10 rounded-full surface-plum flex items-center justify-center">
        <Download size={16} className="text-[hsl(var(--ivory))]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-editorial text-[16px] leading-tight">Keep Knya on your home screen</p>
        <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">
          {iosHint
            ? "Tap Share → Add to Home Screen."
            : "One tap and Knya feels like a native app."}
        </p>
      </div>
      {!iosHint && (
        <button
          onClick={install}
          className="shrink-0 h-9 px-4 rounded-full bg-[hsl(var(--plum))] text-[hsl(var(--ivory))] text-[12px] font-semibold"
          data-testid="install-prompt-install"
        >
          Install
        </button>
      )}
      <button onClick={dismiss} aria-label="Dismiss" className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="install-prompt-dismiss">
        <X size={16} />
      </button>
    </div>
  );
}
